import React, { useRef, useState } from "react";
import { X } from "lucide-react";
import errorToast from "../helper-components/error-toast";
import warningToast from "../helper-components/warning-toast";
import { trayReleaseApi, trayReserveApi } from "../../redux/apis/tray-api";
import { errorMessage, failureMessage } from "../settings/data-setup/tray-management/tray-list-state";

const DOCUMENT_LABELS = { picklist: "pick list", droplist: "drop list" };
const BARCODE_PATTERN = /^\d+$/;

const sameBarcode = (tray, barcode) => String(tray?.barcode) === String(barcode);

// The trays a pick list / drop list (`type` picklist | droplist) holds.
// Read-only on view; on edit, scanning a tray's barcode (the scanner types it
// and presses Enter) reserves it for this document and × releases it.
const DocumentTrays = ({ type, documentId, trays = [], canEdit = false }) => {
  const [heldTrays, setHeldTrays] = useState(trays ?? []);
  const [releasing, setReleasing] = useState([]);
  const [scanValue, setScanValue] = useState("");
  // A scanner can send the next barcode before the last reserve returns
  const reservingRef = useRef(new Set());

  const reserve = (scanned) => {
    if (!BARCODE_PATTERN.test(scanned) || Number(scanned) <= 0) {
      errorToast(`${scanned} is not a tray barcode`);
      return;
    }
    const barcode = String(Number(scanned));
    if (heldTrays.some((tray) => sameBarcode(tray, barcode))) {
      warningToast(`Tray ${barcode} is already on this ${DOCUMENT_LABELS[type]}`);
      return;
    }
    if (reservingRef.current.has(barcode)) return;
    reservingRef.current.add(barcode);
    trayReserveApi(type, documentId, barcode)
      .then((res) => {
        const failed = failureMessage(res);
        if (failed) {
          errorToast(failed);
          return;
        }
        const { tray_id, tray_name, barcode: reserved } = res?.data?.data ?? {};
        setHeldTrays((current) =>
          current.some((tray) => sameBarcode(tray, barcode))
            ? current
            : [...current, { tray_id, tray_name, barcode: reserved ?? barcode }]
        );
      })
      .catch((err) => errorToast(errorMessage(err, `Failed to reserve tray ${barcode}`)))
      .finally(() => reservingRef.current.delete(barcode));
  };

  const release = (tray) => {
    const barcode = String(tray.barcode);
    setReleasing((current) => [...current, barcode]);
    trayReleaseApi(type, documentId, barcode)
      .then((res) => {
        const failed = failureMessage(res);
        if (failed) {
          errorToast(failed);
          return;
        }
        setHeldTrays((current) => current.filter((held) => !sameBarcode(held, barcode)));
      })
      .catch((err) => errorToast(errorMessage(err, `Failed to release ${tray.tray_name}`)))
      .finally(() => setReleasing((current) => current.filter((held) => held !== barcode)));
  };

  const handleScanKeyDown = (event) => {
    if (event.key !== "Enter") return;
    // Enter would otherwise submit the document's form
    event.preventDefault();
    const scanned = event.currentTarget.value.trim();
    setScanValue("");
    if (scanned) reserve(scanned);
  };

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <span className="text-[var(--search-input-label-color)] text-[length:var(--search-label-font-size)] font-[var(--search-label-font-weight)]">
        Trays
      </span>
      {heldTrays.length === 0 && !canEdit && (
        <span className="text-[var(--search-input-color)] text-sm">-</span>
      )}
      {heldTrays.map((tray) => (
        <span
          key={tray.tray_id ?? tray.barcode}
          title={`Barcode ${tray.barcode}`}
          className="inline-flex items-center gap-1 h-[30px] px-2 border border-[var(--search-input-border)] bg-[var(--toggle-disabled-background)] text-[var(--search-input-color)] text-sm rounded-[6px]"
        >
          {tray.tray_name}
          {canEdit && (
            <button
              type="button"
              aria-label={`Release ${tray.tray_name}`}
              disabled={releasing.includes(String(tray.barcode))}
              onClick={() => release(tray)}
              className="flex items-center disabled:opacity-50"
            >
              <X size={13} />
            </button>
          )}
        </span>
      ))}
      {canEdit && (
        <input
          type="text"
          aria-label="Scan tray barcode"
          placeholder="Scan tray barcode"
          value={scanValue}
          onChange={(event) => setScanValue(event.target.value)}
          onKeyDown={handleScanKeyDown}
          className="h-[30px] w-[180px] px-2 border border-[var(--search-input-border)] bg-[var(--input-bg)] text-[var(--input-color)] text-sm rounded-[6px] placeholder:text-[var(--input-place-color)] focus:outline-none focus:border-[var(--input-focus)]"
        />
      )}
    </div>
  );
};

export default DocumentTrays;
